/**
 * Affiliates API client, talks to the Owner Hub backend at `${userApiUrl}/affiliate/*`.
 *
 * `userApiUrl` already ends in `/user`, so the backend's `/user/affiliate/*` routes
 * are reached by appending `/affiliate/*` (never `/user/affiliate/*`, which would
 * double the segment). No new `window.config` key / Helm entry is needed.
 *
 * All calls go through the default axios instance so the global auth interceptor
 * handles 401 refresh-replay + teardown; the raw access token is read fresh from the
 * store per call (so a rotated token is picked up) and sent as `Authorization`
 * (no `Bearer` prefix), matching the other Owner Hub slices.
 *
 * `casino_instance_id` is the Owner Hub casino-instance id (`displayBrand.id`); the
 * backend resolves the platform brand from it (`external_ref = tenant_id`). It goes
 * in the body for the Owner-Hub `POST /access` and the brand-pinned campaign writes,
 * and in the query for the read/passthrough routes.
 */
import axios, { type AxiosRequestConfig } from 'axios';
import { isNil } from 'lodash-es';

import { store } from '@/store';
import { getErrorCode, getErrorMessage } from '@/utils/errors';

import type { DateRange, PeriodPreset } from './reports-helpers';

import type {
  Affiliate,
  AffiliateAccess,
  AffiliateCommissionsReport,
  AffiliateCpaReport,
  AffiliateCreateInput,
  AffiliateCreateResult,
  AffiliatePerformance,
  AffiliateSite,
  AffiliateStatus,
  AffiliateUpdateInput,
  AffiliateCampaign,
  CampaignCreateInput,
  CampaignUpdateInput,
  CommissionAssignment,
  CommissionAssignmentInput,
  CommissionDetachResult,
  CommissionPlan,
  CommissionPlanInput,
  CommissionStructure,
  OrgInvite,
  OrgInviteInput,
  ResourceStatus,
  SiteInput,
} from './types';

/** The `/affiliate` base built from the already-registered `userApiUrl`. Read per call so dev config injection timing never matters. */
export function affiliateApiBase(): string {
  const configured = (window as unknown as { config?: { userApiUrl?: string } }).config?.userApiUrl;
  if (isNil(configured) || configured === '') {
    throw new Error('window.config.userApiUrl is not configured');
  }
  return `${configured.replace(/\/+$/, '')}/affiliate`;
}

/** Pure path builders (exported for tests: they must never produce a doubled `/user`). */
export const affiliatePaths = {
  access: (): string => `${affiliateApiBase()}/access`,
  status: (): string => `${affiliateApiBase()}/status`,
  campaigns: (): string => `${affiliateApiBase()}/campaigns`,
  campaign: (id: string): string => `${affiliateApiBase()}/campaigns/${encodeURIComponent(id)}`,
  campaignStatus: (id: string): string => `${affiliateApiBase()}/campaigns/${encodeURIComponent(id)}/status`,
  affiliates: (): string => `${affiliateApiBase()}/affiliates`,
  affiliate: (id: string): string => `${affiliateApiBase()}/affiliates/${encodeURIComponent(id)}`,
  affiliateStatus: (id: string): string => `${affiliateApiBase()}/affiliates/${encodeURIComponent(id)}/status`,
  sites: (affId: string): string => `${affiliateApiBase()}/affiliates/${encodeURIComponent(affId)}/sites`,
  site: (affId: string, id: string): string =>
    `${affiliateApiBase()}/affiliates/${encodeURIComponent(affId)}/sites/${encodeURIComponent(id)}`,
  reportPerformance: (): string => `${affiliateApiBase()}/reports/performance`,
  reportCpa: (): string => `${affiliateApiBase()}/reports/cpa`,
  reportCommissions: (): string => `${affiliateApiBase()}/reports/commissions`,
  commissionPlans: (): string => `${affiliateApiBase()}/commission-plans`,
  commissionStructure: (affId: string): string =>
    `${affiliateApiBase()}/affiliates/${encodeURIComponent(affId)}/commission-structure`,
  commissionAssignment: (affId: string): string =>
    `${affiliateApiBase()}/affiliates/${encodeURIComponent(affId)}/commission-assignment`,
  invites: (): string => `${affiliateApiBase()}/invites`,
  inviteRevoke: (id: string): string => `${affiliateApiBase()}/invites/${encodeURIComponent(id)}/revoke`,
};

function authHeaders(): Record<string, string> {
  const token = store.getState().authentication.access_token;
  if (isNil(token)) throw new Error('No authentication token found');
  return { Authorization: token };
}

/**
 * The shared axios config for EVERY affiliate call: the fresh raw `Authorization` header, an optional
 * `params` object, and `_owsNoAuthTeardown`.
 *
 * The affiliate routes proxy a DOWNSTREAM vendor (afilliates.bet), so a 401/403 from them (an expired
 * vendor session, a "missing scope" rejection like `sites:add`, a vendor-side denial) is NOT evidence
 * the owner-hub access token expired. Without this flag the global auth interceptor tears the owner-hub
 * session down and logs the owner out over an unrelated downstream error (then the next owner-hub call,
 * e.g. the composer's video autosave, 401s with a cleared token). `_owsNoAuthTeardown` still lets a
 * genuine owner-hub token expiry self-heal (refresh + replay) but, when that fails, rejects to the
 * caller (surfaced via {@link getAffiliateError}) instead of ejecting them. Exported for tests.
 */
export function affiliateConfig(params?: Record<string, unknown>): AxiosRequestConfig {
  return { headers: authHeaders(), _owsNoAuthTeardown: true, ...(params ? { params } : {}) };
}

/** A normalized error: a human `message` for the toast + an optional machine `code` to branch on. */
export interface AffiliateApiError {
  message: string;
  code?: string;
}

/**
 * Extract a message + code across BOTH failure shapes: the partner envelope
 * (`{ ok: false, error: <string>, message }`, forwarded by the proxy routes) and
 * the Owner Hub error (`{ error: { code, message } }` on 500, used by /access, /status).
 */
export function getAffiliateError(error: unknown): AffiliateApiError {
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (data !== null && typeof data === 'object' && 'ok' in data && (data as { ok?: unknown }).ok === false) {
    const envelope = data as { error?: unknown; message?: unknown };
    const message = typeof envelope.message === 'string' ? envelope.message : undefined;
    const errStr = typeof envelope.error === 'string' ? envelope.error : undefined;
    return { message: message ?? errStr ?? getErrorMessage(error), code: errStr };
  }
  return { message: getErrorMessage(error), code: getErrorCode(error) };
}

/** Unwrap the partner success envelope `{ ok, data }`; tolerate a body that is already unwrapped. */
function unwrapEnvelope(body: unknown): unknown {
  if (body !== null && typeof body === 'object' && 'ok' in body && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

/** A list may arrive as `data: [...]` or as `data: { <key>: [...] }` (both observed). Accept either. */
function unwrapList<T>(payload: unknown, keys: string[]): T[] {
  const data = unwrapEnvelope(payload);
  if (Array.isArray(data)) return data as T[];
  if (data !== null && typeof data === 'object') {
    for (const key of keys) {
      const value = (data as Record<string, unknown>)[key];
      if (Array.isArray(value)) return value as T[];
    }
  }
  return [];
}

/** A single resource may arrive flat or wrapped as `data: { <key>: {...} }`. */
function unwrapOne<T>(payload: unknown, key: string): T {
  const data = unwrapEnvelope(payload);
  if (data !== null && typeof data === 'object' && key in data) {
    const value = (data as Record<string, unknown>)[key];
    if (value !== null && typeof value === 'object') return value as T;
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Access + status (Owner Hub JSON)
// ---------------------------------------------------------------------------

/** Console path the SSO should land on for a given affiliate. This is the value of the `/access`
 *  deep-link `next` param (the SSO redirect target). The console resolves affiliates by their SHORT
 *  affiliate NUMBER (`affiliate_no`, e.g. 100013), NOT the UUID id, so the path uses that number.
 *  Single source of truth so the exact path is a one-line change if the console route changes. */
export const affiliateConsoleNext = (affiliateNo: number | string): string =>
  `/admin/affiliates/${encodeURIComponent(String(affiliateNo))}`;

/** Console path the SSO should land on from the Affiliates page: the console's Overview on `period`. The console reads
 *  `period` for a preset it shares (this month, 7 / 30 / 90 days, rolling with the days), else the inclusive local days
 *  `from`..`to`, so both are sent for a preset and only the days for a custom run. */
export const affiliateConsoleOverviewNext = ({ preset, range }: { preset: PeriodPreset; range: DateRange }): string => {
  const query = new URLSearchParams();
  if (preset !== 'custom') query.set('period', preset);
  query.set('from', range.from);
  query.set('to', range.to);
  return `/admin/overview?${query.toString()}`;
};

/**
 * Provision what is missing and return a single-use, 5-minute SSO url into the console.
 *
 * With `next` (a console path, see {@link affiliateConsoleNext} and {@link affiliateConsoleOverviewNext}) the SSO
 * lands there: the backend forwards it to the vendor login-token `next`. Without it the link lands on the brand
 * console root.
 */
export async function requestAccess(casinoInstanceId: string, next?: string): Promise<AffiliateAccess> {
  const res = await axios.post(
    affiliatePaths.access(),
    { casino_instance_id: casinoInstanceId, ...(isNil(next) ? {} : { next }) },
    affiliateConfig(),
  );
  return res.data as AffiliateAccess;
}

/** Whether `/access` refused the request's `next` (422 now, 400 on older deploys): the one failure a retry without the
 *  deep-link target can fix. Any other failure (an unverified e-mail, the platform down, a timeout) fails the same way
 *  without it, so retrying would only provision again and keep the owner waiting twice as long. */
export const accessRefusedNext = (err: unknown): boolean => {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  return status === 400 || status === 422;
};

/**
 * {@link requestAccess} with the deep-link target as best effort: when `/access` refuses `next` (see
 * {@link accessRefusedNext}) it asks again without it, for the brand console root, so the console button never breaks
 * on a target the backend doesn't support. Any other failure is thrown as it is, at once.
 */
export async function requestAccessFallingBack(casinoInstanceId: string, next?: string): Promise<AffiliateAccess> {
  try {
    return await requestAccess(casinoInstanceId, next);
  } catch (err: unknown) {
    if (next === undefined || !accessRefusedNext(err)) throw err;
    return requestAccess(casinoInstanceId);
  }
}

/** Read the stored link state (no remote calls, no provisioning). */
export async function fetchStatus(casinoInstanceId?: string): Promise<AffiliateStatus> {
  const res = await axios.get(
    affiliatePaths.status(),
    affiliateConfig(isNil(casinoInstanceId) ? undefined : { casino_instance_id: casinoInstanceId }),
  );
  return res.data as AffiliateStatus;
}

// ---------------------------------------------------------------------------
// Affiliates (proxy)
// ---------------------------------------------------------------------------

export async function listAffiliates(casinoInstanceId: string): Promise<Affiliate[]> {
  const res = await axios.get(affiliatePaths.affiliates(), affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapList<Affiliate>(res.data, ['affiliates']);
}

export async function createAffiliate(
  casinoInstanceId: string,
  input: AffiliateCreateInput,
): Promise<AffiliateCreateResult> {
  const res = await axios.post(affiliatePaths.affiliates(), input, affiliateConfig({ casino_instance_id: casinoInstanceId }));
  const data = unwrapEnvelope(res.data);
  // Create returns `{ affiliate, invite? }`; tolerate a flat affiliate body too.
  if (data !== null && typeof data === 'object' && 'affiliate' in data) {
    return data as AffiliateCreateResult;
  }
  return { affiliate: data as Affiliate };
}

export async function updateAffiliate(
  casinoInstanceId: string,
  id: string,
  input: AffiliateUpdateInput,
): Promise<Affiliate> {
  const res = await axios.patch(affiliatePaths.affiliate(id), input, affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapOne<Affiliate>(res.data, 'affiliate');
}

export async function setAffiliateStatus(
  casinoInstanceId: string,
  id: string,
  status: ResourceStatus,
): Promise<Affiliate> {
  const res = await axios.patch(
    affiliatePaths.affiliateStatus(id),
    { status },
    affiliateConfig({ casino_instance_id: casinoInstanceId }),
  );
  return unwrapOne<Affiliate>(res.data, 'affiliate');
}

// ---------------------------------------------------------------------------
// Site-ids (proxy, nested under an affiliate)
// ---------------------------------------------------------------------------

export async function listSites(casinoInstanceId: string, affiliateId: string): Promise<AffiliateSite[]> {
  const res = await axios.get(affiliatePaths.sites(affiliateId), affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapList<AffiliateSite>(res.data, ['sites']);
}

export async function createSite(
  casinoInstanceId: string,
  affiliateId: string,
  input: SiteInput,
): Promise<AffiliateSite> {
  const res = await axios.post(affiliatePaths.sites(affiliateId), input, affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapOne<AffiliateSite>(res.data, 'site');
}

export async function updateSite(
  casinoInstanceId: string,
  affiliateId: string,
  siteId: string,
  input: Partial<SiteInput> & { status?: ResourceStatus },
): Promise<AffiliateSite> {
  const res = await axios.patch(affiliatePaths.site(affiliateId, siteId), input, affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapOne<AffiliateSite>(res.data, 'site');
}

// ---------------------------------------------------------------------------
// Campaigns (proxy, brand-pinned by the backend)
// ---------------------------------------------------------------------------

export async function listCampaigns(casinoInstanceId: string): Promise<AffiliateCampaign[]> {
  const res = await axios.get(affiliatePaths.campaigns(), affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapList<AffiliateCampaign>(res.data, ['campaigns']);
}

export async function createCampaign(
  casinoInstanceId: string,
  input: CampaignCreateInput,
): Promise<AffiliateCampaign> {
  const res = await axios.post(
    affiliatePaths.campaigns(),
    { casino_instance_id: casinoInstanceId, ...input },
    affiliateConfig(),
  );
  return unwrapOne<AffiliateCampaign>(res.data, 'campaign');
}

export async function updateCampaign(
  casinoInstanceId: string,
  id: string,
  input: CampaignUpdateInput,
): Promise<AffiliateCampaign> {
  const res = await axios.patch(
    affiliatePaths.campaign(id),
    { casino_instance_id: casinoInstanceId, ...input },
    affiliateConfig(),
  );
  return unwrapOne<AffiliateCampaign>(res.data, 'campaign');
}

export async function setCampaignStatus(
  casinoInstanceId: string,
  id: string,
  status: ResourceStatus,
): Promise<AffiliateCampaign> {
  const res = await axios.patch(
    affiliatePaths.campaignStatus(id),
    { casino_instance_id: casinoInstanceId, status },
    affiliateConfig(),
  );
  return unwrapOne<AffiliateCampaign>(res.data, 'campaign');
}

// ---------------------------------------------------------------------------
// Reports (proxy, read-only)
// ---------------------------------------------------------------------------

export interface PerformanceQuery {
  from?: string;
  to?: string;
  group_by?: 'date' | 'affiliate' | 'campaign' | 'brand' | 'total';
  products?: 0 | 1;
  affiliate_id?: string;
  brand_id?: string;
  campaign?: string;
}

export async function fetchPerformance(
  casinoInstanceId: string,
  query: PerformanceQuery = {},
): Promise<AffiliatePerformance> {
  const res = await axios.get(
    affiliatePaths.reportPerformance(),
    affiliateConfig({ casino_instance_id: casinoInstanceId, ...query }),
  );
  const data = unwrapEnvelope(res.data);
  if (Array.isArray(data)) return { rows: data as AffiliatePerformance['rows'] };
  return (data ?? {}) as AffiliatePerformance;
}

export async function fetchCpaReport(casinoInstanceId: string): Promise<AffiliateCpaReport> {
  const res = await axios.get(affiliatePaths.reportCpa(), affiliateConfig({ casino_instance_id: casinoInstanceId }));
  const data = unwrapEnvelope(res.data);
  if (Array.isArray(data)) return { affiliates: data as AffiliateCpaReport['affiliates'] };
  return (data ?? {}) as AffiliateCpaReport;
}

export async function fetchCommissions(
  casinoInstanceId: string,
  month?: string,
): Promise<AffiliateCommissionsReport> {
  const res = await axios.get(
    affiliatePaths.reportCommissions(),
    affiliateConfig({ casino_instance_id: casinoInstanceId, ...(isNil(month) ? {} : { month }) }),
  );
  const data = unwrapEnvelope(res.data);
  if (Array.isArray(data)) return { affiliates: data as AffiliateCommissionsReport['affiliates'] };
  return (data ?? {}) as AffiliateCommissionsReport;
}

// ---------------------------------------------------------------------------
// Commission structures (proxy: create a plan, read/assign/detach for one affiliate)
// ---------------------------------------------------------------------------

/** Read the commission plan(s) assigned to one of your affiliates. Empty structure is a normal state. With `brandId`
 *  the answer also carries `resolved`, the one assignment in force for that brand. */
export async function fetchCommissionStructure(
  casinoInstanceId: string,
  affiliateId: string,
  brandId?: string,
): Promise<CommissionStructure> {
  const res = await axios.get(
    affiliatePaths.commissionStructure(affiliateId),
    affiliateConfig({ casino_instance_id: casinoInstanceId, ...(brandId ? { brand_id: brandId } : {}) }),
  );
  const data = unwrapEnvelope(res.data);
  if (data !== null && typeof data === 'object') return data as CommissionStructure;
  return { assignments: [] };
}

/** Create a commission plan (owned by this key). Returns the created plan (with its id). */
export async function createCommissionPlan(
  casinoInstanceId: string,
  input: CommissionPlanInput,
): Promise<CommissionPlan> {
  const res = await axios.post(
    affiliatePaths.commissionPlans(),
    input,
    affiliateConfig({ casino_instance_id: casinoInstanceId }),
  );
  return unwrapOne<CommissionPlan>(res.data, 'plan');
}

/**
 * Attach / move an affiliate onto a plan you created. A 409 `would_replace` (needs
 * `confirm_replace`), a 409 `foreign_assignment` (a console-managed plan, cannot be replaced here)
 * and a 422 `out_of_boundary` all surface via {@link getAffiliateError}'s `code`.
 */
export async function assignCommission(
  casinoInstanceId: string,
  affiliateId: string,
  input: CommissionAssignmentInput,
): Promise<CommissionAssignment> {
  // The "all brands" default MUST omit brand_id entirely, not send null: the backend casts a
  // null/empty brand_id to uuid and 500s ("invalid input syntax for type uuid"). Only include
  // brand_id when it is a real id (a future brand-scoped assignment).
  const { brand_id, ...rest } = input;
  const body = isNil(brand_id) ? rest : { ...rest, brand_id };
  const res = await axios.put(
    affiliatePaths.commissionAssignment(affiliateId),
    body,
    affiliateConfig({ casino_instance_id: casinoInstanceId }),
  );
  return unwrapOne<CommissionAssignment>(res.data, 'assignment');
}

// ---------------------------------------------------------------------------
// Org invites (proxy: a shareable self-registration link, distinct from the per-affiliate token)
// ---------------------------------------------------------------------------

/** List the org's invites (needs the key's `invites:view` scope). */
export async function listInvites(casinoInstanceId: string): Promise<OrgInvite[]> {
  const res = await axios.get(affiliatePaths.invites(), affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapList<OrgInvite>(res.data, ['invites']);
}

/** Mint a shareable invite link (needs `invites:add`). API-created invites default to auto-approve. */
export async function createInvite(casinoInstanceId: string, input: OrgInviteInput): Promise<OrgInvite> {
  const res = await axios.post(affiliatePaths.invites(), input, affiliateConfig({ casino_instance_id: casinoInstanceId }));
  return unwrapOne<OrgInvite>(res.data, 'invite');
}

/**
 * Revoke a pending org invite by id (a revoked invite can no longer be redeemed). Route per the Partner
 * API doc and confirmed by probe: `POST /affiliate/invites/{id}/revoke` -> `POST /ext/v1/invites/{id}/revoke`.
 * Needs the key's `invites:edit` scope (distinct from createInvite's `invites:add`), so a key without it
 * will 403 here even though it can create invites.
 */
export async function revokeInvite(casinoInstanceId: string, id: string): Promise<void> {
  await axios.post(affiliatePaths.inviteRevoke(id), {}, affiliateConfig({ casino_instance_id: casinoInstanceId }));
}

/** Detach the "all brands" default (no `brandId`) or a specific brand's assignment. Idempotent. */
export async function detachCommission(
  casinoInstanceId: string,
  affiliateId: string,
  brandId?: string,
): Promise<CommissionDetachResult> {
  const res = await axios.delete(
    affiliatePaths.commissionAssignment(affiliateId),
    affiliateConfig({ casino_instance_id: casinoInstanceId, ...(isNil(brandId) ? {} : { brand_id: brandId }) }),
  );
  const data = unwrapEnvelope(res.data);
  if (data !== null && typeof data === 'object') return data as CommissionDetachResult;
  return { detached: false };
}
